// STAGING-LAUNCH-READINESS-002-R2-R2 — shared route-loading gate.
// Two independent timers layered on top of a boolean `isLoading`:
//  - skeletonDelayMs (~150ms): avoid a spinner flash on fast responses — the
//    route shell renders immediately regardless, only the inner placeholder
//    is delayed.
//  - timeoutMs (default 8s): flips a "degraded" flag so the caller can swap
//    the spinner for a Retry + Home affordance instead of spinning forever.
import { useEffect, useState } from "react";

export interface RouteLoadingGate {
  showSkeleton: boolean;
  timedOut: boolean;
}

export function useRouteLoadingGate(
  isLoading: boolean,
  opts?: { skeletonDelayMs?: number; timeoutMs?: number },
): RouteLoadingGate {
  const skeletonDelayMs = opts?.skeletonDelayMs ?? 150;
  const timeoutMs = opts?.timeoutMs ?? 8000;
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShowSkeleton(false);
      setTimedOut(false);
      return;
    }
    setShowSkeleton(false);
    setTimedOut(false);
    const skeletonTimer = window.setTimeout(() => setShowSkeleton(true), skeletonDelayMs);
    const timeoutTimer = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => {
      window.clearTimeout(skeletonTimer);
      window.clearTimeout(timeoutTimer);
    };
    // Re-arm only when the loading state itself flips (a new attempt), not on
    // every render — the timers below are aimed at THIS loading attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return { showSkeleton, timedOut };
}
